import React, { useState } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  ClipboardCheck,
  Thermometer,
  SprayCan,
  AlertTriangle,
  Shield,
  FileText,
  HardHat,
  Lock,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";
import moment from "moment";

// Status constants
const STATUS = {
  OK: "ok",
  WARN: "warn",
  PENDING: "pending",
};

function statusStyle(s) {
  if (s === STATUS.OK) return { dot: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Correcte" };
  if (s === STATUS.WARN) return { dot: "bg-amber-400", badge: "bg-amber-50 text-amber-700 border-amber-200", label: "Revisar" };
  return { dot: "bg-red-500", badge: "bg-red-50 text-red-700 border-red-200", label: "Pendent" };
}

function StatusBadge({ status }) {
  const s = statusStyle(status);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${s.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function InspectionRow({ icon: Icon, label, page, status, detail }) {
  const s = statusStyle(status);
  return (
    <Link to={createPageUrl(page)}>
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group cursor-pointer border-b border-gray-50 last:border-0">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
          status === STATUS.OK ? "bg-emerald-50" : status === STATUS.WARN ? "bg-amber-50" : "bg-red-50"
        }`}>
          <Icon className={`w-4 h-4 ${
            status === STATUS.OK ? "text-emerald-600" : status === STATUS.WARN ? "text-amber-600" : "text-red-600"
          }`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800">{label}</p>
          {detail && <p className="text-xs text-gray-400 mt-0.5 truncate">{detail}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge status={status} />
          <ExternalLink className="w-3 h-3 text-gray-300 group-hover:text-gray-500 transition-colors" />
        </div>
      </div>
    </Link>
  );
}

// Overall status from array of statuses
function overallStatus(statuses) {
  if (statuses.some(s => s === STATUS.PENDING)) return STATUS.PENDING;
  if (statuses.some(s => s === STATUS.WARN)) return STATUS.WARN;
  return STATUS.OK;
}

export default function InspectionMode({ venueId, data, today }) {
  const [expanded, setExpanded] = useState(true);
  if (!data) return null;

  const now = moment();

  // ── 1. Temperatures ─────────────────────────────────────────────────
  const tempTotal = data.temps?.length || 0;
  const incorrectTemps = data.temps?.filter(t => t.status === "incorrect").length || 0;
  const tempStatus = tempTotal === 0
    ? STATUS.PENDING
    : incorrectTemps > 0 ? STATUS.WARN : STATUS.OK;
  const tempDetail = tempTotal === 0
    ? "Sense registres avui"
    : incorrectTemps > 0 ? `${incorrectTemps} fora de rang` : `${tempTotal} registres correctes`;

  // ── 2. Neteja ────────────────────────────────────────────────────────
  const cleanTotal = data.cleaning?.length || 0;
  const cleanDone = data.completedCleaning?.length || 0;
  const cleanPct = cleanTotal > 0 ? Math.round((cleanDone / cleanTotal) * 100) : 0;
  const cleanStatus = cleanTotal === 0
    ? STATUS.PENDING
    : cleanPct === 100 ? STATUS.OK : cleanPct >= 50 ? STATUS.WARN : STATUS.PENDING;
  const cleanDetail = cleanTotal === 0
    ? "Sense tasques avui"
    : `${cleanDone}/${cleanTotal} completades (${cleanPct}%)`;

  // ── 3. Incidències ───────────────────────────────────────────────────
  const openInc = data.openIncidents?.length || 0;
  const criticalInc = data.criticalIncidents?.length || 0;
  const incStatus = criticalInc > 0 ? STATUS.PENDING : openInc > 0 ? STATUS.WARN : STATUS.OK;
  const incDetail = openInc === 0
    ? "Cap incidència oberta"
    : `${openInc} obertes${criticalInc > 0 ? `, ${criticalInc} crítiques` : ""}`;

  // ── 4. APPCC / Verificacions ─────────────────────────────────────────
  const appccAlerts = data.alerts?.filter(a =>
    a.reference_type?.startsWith("safety_doc") || a.type === "document_expiry"
  ).length || 0;
  const appccStatus = appccAlerts > 0 ? STATUS.WARN : STATUS.OK;
  const appccDetail = appccAlerts > 0 ? `${appccAlerts} avisos actius` : "Sense avisos actius";

  // ── 5. Documents ─────────────────────────────────────────────────────
  const expiringDocs = data.expiringDocs?.length || 0;
  const expiredDocs = data.expiringDocs?.filter(d => moment(d.expiry_date).isBefore(now, "day")).length || 0;
  const docsStatus = expiredDocs > 0 ? STATUS.PENDING : expiringDocs > 0 ? STATUS.WARN : STATUS.OK;
  const docsDetail = expiringDocs === 0
    ? "Documents al dia"
    : `${expiredDocs > 0 ? `${expiredDocs} caducats, ` : ""}${expiringDocs} per renovar`;

  // ── 6. Riscos laborals (PRL) ─────────────────────────────────────────
  const prlAlerts = data.alerts?.filter(a =>
    ["safety_doc_expired", "safety_doc_expiring", "safety_training_expired", "safety_training_expiring",
      "equipment_maint_overdue", "equipment_maint_due"].includes(a.reference_type)
  ).length || 0;
  const prlCritical = data.alerts?.filter(a =>
    ["safety_doc_expired", "safety_training_expired", "equipment_maint_overdue"].includes(a.reference_type)
  ).length || 0;
  const prlStatus = prlCritical > 0 ? STATUS.PENDING : prlAlerts > 0 ? STATUS.WARN : STATUS.OK;
  const prlDetail = prlAlerts === 0
    ? "Documents i formació al dia"
    : `${prlCritical > 0 ? `${prlCritical} caducats, ` : ""}${prlAlerts} avisos`;

  // ── 7. Protecció de dades (RGPD) ─────────────────────────────────────
  const rgpdAlerts = data.alerts?.filter(a => a.type === "rgpd").length || 0;
  const rgpdStatus = rgpdAlerts > 0 ? STATUS.WARN : STATUS.OK;
  const rgpdDetail = rgpdAlerts > 0 ? `${rgpdAlerts} avisos actius` : "Sense incidències actives";

  const rows = [
    { icon: Thermometer, label: "Temperatures", page: "Temperatures", status: tempStatus, detail: tempDetail },
    { icon: SprayCan, label: "Neteja", page: "Cleaning", status: cleanStatus, detail: cleanDetail },
    { icon: AlertTriangle, label: "Incidències", page: "Incidents", status: incStatus, detail: incDetail },
    { icon: Shield, label: "Verificacions APPCC", page: "APPCCManagement", status: appccStatus, detail: appccDetail },
    { icon: FileText, label: "Documents", page: "Documents", status: docsStatus, detail: docsDetail },
    { icon: HardHat, label: "Riscos Laborals (PRL)", page: "WorkerSafetyManagement", status: prlStatus, detail: prlDetail },
    { icon: Lock, label: "Protecció de Dades", page: "GDPRManagement", status: rgpdStatus, detail: rgpdDetail },
  ];

  const global = overallStatus(rows.map(r => r.status));
  const pendingCount = rows.filter(r => r.status === STATUS.PENDING).length;
  const warnCount = rows.filter(r => r.status === STATUS.WARN).length;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="w-9 h-9 rounded-xl bg-[#f9f0f2] flex items-center justify-center flex-shrink-0">
          <ClipboardCheck className="w-[18px] h-[18px] text-[#7B2D3F]" />
        </div>
        <div className="flex-1 text-left">
          <h3 className="font-bold text-gray-900 text-sm">Mode Inspecció</h3>
          <p className="text-xs text-gray-400">Estat global del restaurant</p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{pendingCount} pendent{pendingCount > 1 ? "s" : ""}</span>
          )}
          {warnCount > 0 && (
            <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{warnCount} a revisar</span>
          )}
          <StatusBadge status={global} />
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {/* Rows */}
      {expanded && (
        <div className="border-t border-gray-100">
          {rows.map(row => (
            <InspectionRow key={row.page} {...row} />
          ))}
        </div>
      )}
    </div>
  );
}